// MobileTopBar — the mobile stylist app's top bar (C8, bead jonathan-ydfw).
//
// Faithful port of the "app top bar" in Jonathan-Mobile.dc.html: the italic
// Playfair "Jonathan" wordmark on the left, a circular olive-gradient avatar
// with the salon/stylist initial on the right. In the .dc.html this lives at
// the very top of the phone frame, under the status bar; in the real app it is
// the persistent header above the tabbed views, paired with the bottom TabBar.
//
// The desktop .dc.html has no equivalent top bar (its IA sits in a wide top
// nav), so this header is mobile-shaped: it only renders on narrow viewports
// (handled by the caller's wrapper class). It reuses the C1 tokens/fonts — no
// new palette, no dual-path. The avatar initial is derived from the salon name
// (the only real identity the dashboard payload carries today; the .dc.html's
// per-stylist "A" is mock — we degrade to the salon initial, never invent a
// stylist identity that the backend does not surface).
//
// The avatar is the account chip (bead jonathan-exp8): tapping it opens a small
// menu showing the salon/account name + a Log out action. Log out is the
// canonical dashboard logout (api/client.logout) — clears the SESSION cookie
// and redirects to /account/login; the same helper the "Your salon" account
// row uses, so sign-out lives in exactly one place.

import { useState, useEffect, useRef } from 'preact/hooks';
import { logout } from '../api/client';
import { ASSISTANT_INITIAL, ASSISTANT_NAME } from '../brand.js';

function avatarInitial(name) {
  if (!name) return ASSISTANT_INITIAL;
  const trimmed = String(name).trim();
  return trimmed ? trimmed[0].toUpperCase() : ASSISTANT_INITIAL;
}

export function MobileTopBar({ salonName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  // Dismiss the account menu on outside-tap or Escape — standard popover
  // affordances so the menu never traps focus over the tabbed views.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header
      class="mobile-top-bar"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
        boxSizing: 'border-box',
        // Deterministic bar geometry. The bar is a fixed overlay, so the
        // tabbed views beneath it must reserve a KNOWN height to start their
        // content below the wordmark — earlier this was a magic 56px in
        // index.css that one view satisfied and SettingsView did not, so the
        // wordmark crushed the salon name. The height is now the single
        // source of truth (--mobile-top-bar-h, content box) + the device's
        // safe-area inset, and the reservation in index.css reads the same
        // token. The avatar/wordmark are vertically centered in that height,
        // so neither can collide regardless of font metrics.
        height: 'calc(var(--mobile-top-bar-h) + var(--safe-area-top))',
        paddingTop: 'var(--safe-area-top)',
        paddingLeft: '22px',
        paddingRight: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        background: 'var(--bg)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <span
        class="font-serif"
        style={{
          fontStyle: 'italic',
          fontWeight: 600,
          fontSize: '23px',
          color: 'var(--ink)',
          // The wordmark is centered in the bar's flex row; give it normal
          // baseline breathing room (not lineHeight:1) so its descenders are
          // not clipped against the bar's bottom border.
          lineHeight: 1.1,
          flex: 'none',
        }}
      >
        {ASSISTANT_NAME}
      </span>

      {/* Account chip + menu (bead jonathan-exp8). The avatar is the anchor;
          the menu is absolutely positioned under it within this wrapper so
          outside-tap dismissal has a single containing node. */}
      <div ref={wrapRef} style={{ position: 'relative', flex: 'none' }}>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Account menu"
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            width: '34px',
            height: '34px',
            padding: 0,
            border: 'none',
            cursor: 'pointer',
            borderRadius: '50%',
            background: 'linear-gradient(140deg,#56683F,#33401E)',
            color: '#F3EFE6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '13px',
          }}
        >
          {avatarInitial(salonName)}
        </button>

        {menuOpen && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 10px)',
              right: 0,
              minWidth: '200px',
              background: 'var(--cream-light)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '14px',
              boxShadow: '0 12px 32px rgba(34,30,23,0.18)',
              overflow: 'hidden',
              zIndex: 40,
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <div
                style={{
                  fontSize: '0.6875rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--text-secondary)',
                  marginBottom: '2px',
                }}
              >
                Signed in
              </div>
              <div
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  color: 'var(--ink)',
                  lineHeight: 1.2,
                }}
              >
                {salonName || 'Your salon'}
              </div>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: 'var(--terracotta, #C5512F)',
              }}
            >
              Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
